import Vue from 'vue';
import Storage from '@/storage/Storage';
import JwtDecode from 'jwt-decode';
import _ from 'underscore';

export default {
	state: () => ({
		user: {},
		currentAccountId: null,
		accountsById: {},
		token: null,
		pendingInvitation: null,
		extensions: [],
	}),

	getters: {

		currentUser(state) {
			return (state.user && state.user.id) ? state.user : {};
		},

		extensions(state) {
			return state.extensions;
		},

		masterAccount(state) {
			return _.findWhere(Object.values(state.accountsById) || {}, { master: true });
		},

		isSuperAdmin(state) {
			return !!(state.user && state.user.access_level === 'Super');
		},

		currentAccount(state) {
			return state.accountsById[ state.currentAccountId ] || {};
		},

		accounts(state) {
			return Object.values(state.accountsById || {});
		},

		inSetupGuide(state, getters) {
			return getters.currentAccount && !getters.currentAccount.setup_completed;
		},

		isLoggedIn(state) {
			return !!(state.user && state.user.id);
		},

		token(state) {
			return state.token;
		},

		hasPendingInvitation(state) {
			return !!state.pendingInvitation && !!state.pendingInvitation.responseUrl;
		},

		pendingInvitation(state) {
			return state.pendingInvitation;
		},

		canManageBilling(state, getters) {
			if (!getters.currentAccount) return false;

			if (getters.currentAccount.account_owner_id === state.user.id) return true;

			return getters.currentAccount.access && getters.currentAccount.access.has_admin_access && getters.currentAccount.access.can_manage_billing;
		},

		canManageUsers(state, getters) {
			if (!getters.currentAccount) return false;

			if (!getters.currentAccount.access) return false;

			return getters.currentAccount.access.access_type === 'ADMIN' || getters.currentAccount.access.can_manage_users;
		},

		currentSetupStep(state, getters) {
			if ((!getters.currentAccount || !getters.currentAccount.setup_steps) || getters.currentAccount.setup_completed) return { name: 'finished' };

			for (let i = 0; i < getters.currentAccount.setup_steps.length; i++) {
				const step = getters.currentAccount.setup_steps[ i ];
				if (step.status !== 'completed') return step;
			}

			return { name: 'finished' };
		},

		isEmailVerified(state) {
			return !!state.user.email_verified_at;
		},

	},

	mutations: {

		setToken(state, token) {
			Vue.set(state, 'token', token);
			Storage.set('token', token, { preferCookies: true });
		},

		setUser(state, user) {
			Vue.set(state, 'user', user);
			Storage.set('user', user, { expires: 180, preferCookies: true });
		},

		setCurrentAccount(state, accountId) {
			Vue.set(state, 'currentAccountId', accountId);
			Storage.set('account', state.accountsById[ accountId ] || {}, { expires: 180, preferCookies: true });
		},

		completeSetup(state) {
			Vue.set(state.accountsById, state.currentAccountId, {
				...(state.accountsById[ state.currentAccountId ] || {}),
				setup_completed: true
			});
			Storage.set('account', state.accountsById[ state.currentAccountId ] || {}, { expires: 180, preferCookies: true });
		},

		/**
		 * setAccounts expects an object hash of accounts keyed by accountId
		 * @param state
		 * @param accounts
		 */
		setAccounts(state, accounts) {
			Vue.set(state, 'accountsById', _.isArray(accounts) ? _.indexBy(accounts, 'id') : accounts);
		},

		setAccount(state, account) {
			Vue.set(state.accountsById, account.id, {
				...(state.accountsById[ account.id ] || {}),
				...account || {}
			});
		},

		setExtensions(state, extensions) {
			Vue.set(state, 'extensions', extensions);
		},

		logout(state) {
			Vue.set(state, 'user', {});

			Storage.remove('token');
			Storage.remove('user');
			Storage.remove('account');
		},

		setPendingInvitation(state, pendingInvite) {
			state.pendingInvitation = pendingInvite;
			Storage.set('pendingInvitation', pendingInvite, { expires: 180, preferCookies: true });
		},

		clearPendingInvitation(state) {
			state.pendingInvitation = null;
			Storage.remove('pendingInvitation');
		},

		updateStep(state, { label, status }) {
			const account = state.accountsById[ state.currentAccountId ];

			if (account && account.setup_steps) {
				const index = _.findIndex(account.setup_steps, { label });
				if (index !== -1) {
					Vue.set(state.accountsById[ state.currentAccountId ].setup_steps, index, {
						...account.setup_steps[ index ] || {},
						label,
						status,
					});
				}

			}
		},

	},

	actions: {

		async getAccounts({ commit, dispatch }) {
			try {
				const { data } = await this.$api.get('/user');

				commit('setAccounts', data.data.accounts);
				dispatch('setUser', _.omit(data.data, 'accounts'));
				// dispatch('setCurrentAccount', data.data.last_account_used);
			} catch (e) { console.warn('TODO - Inform we couldnt load an account'); }
		},

		setToken({ commit }, token) {
			commit('setToken', token);
		},

		setExtensions({ commit }, extensions) {
			commit('setExtensions', extensions);
		},

		setUser({ commit }, user) {
			commit('setUser', user);
		},

		setAccounts({ commit }, accounts) {
			commit('setAccounts', accounts);
		},

		setCurrentAccount({ commit }, accountId) {
			commit('setCurrentAccount', accountId);
		},

		async switchAccount({ dispatch }, newAccountId) {
			try {
				const response = await this.$api.post('/accounts/' + newAccountId + '/switch');
				const data = response.data;

				dispatch('wipeAccountData');
				dispatch('login', data);
				await dispatch('syncAccountData');

				return true;
			} catch (e) {}

			return false;
		},

		// Meta Action - Only calls other actions
		login({ dispatch }, { account_id, user, accounts }) {
			if (accounts) {
				dispatch('setAccounts', accounts);
				if (!account_id && accounts.length > 0) {
					account_id = accounts[0].id;
				}
			}

			console.log('account id', account_id, arguments);
			dispatch('setToken', 'blah');
			dispatch('setCurrentAccount', account_id);
			dispatch('setUser', user);
			// dispatch('updateBrandColor', { hex: (user || {}).brand_color || '#6150f6', store: true }, { root: true });
			// dispatch('updateTheme', { theme: user.theme || 'dark', store: true }, { root: true });
		},

		async logout({ commit, dispatch }) {
			await this.$api.post('/logout');
			dispatch('setToken', null);
			dispatch('wipeAccountData');
			commit('logout');
		},

		setPendingInvitation({ commit }, pendingInvite) {
			commit('setPendingInvitation', pendingInvite);
		},

		async loadPendingAuth({ commit }) {
			let pendingInvitation = await Storage.get('pendingInvitation');
			if (pendingInvitation) {
				commit('setPendingInvitation', pendingInvitation);
			}
		},

		invalidAccount({ dispatch, getters }) {
			// Switch to Another Account
			dispatch('getAccounts');

			const newAccount = _.find(getters.accounts, (account) => {
				return account.id !== getters.currentAccount.id;
			});

			if (!newAccount) {
				// TODO - Log to rollbar this user has no accessible accounts, high priority

				dispatch('addToast', 'There is an issue with your account. Please contact support.', { root: true });
				dispatch('logout');

				return;
			}

			dispatch('switchAccount', newAccount.id);
		},

		setupStepStatus({ commit }, stepState) {
			commit('updateStep', stepState);
		},

		setupStepComplete({ commit }, label) {
			commit('updateStep', { label, status: 'completed' });
		},

		accountUpdated({ commit, getters }, account) {
			commit('setAccount', account);
			if (getters.currentAccount.id === account.id) {
				commit('setCurrentAccount', account.id);
			}
		},

		async abandonSetup({ commit }) {
			try {
				await this.$api.post('/account/complete-setup');

				commit('completeSetup');

			} catch (e) {}
		}

	},
};
